import { useForm } from "@inertiajs/react"
import { MessageSquare } from "lucide-react"
import { useState, type FormEvent } from "react"

import { Field, fieldErrorId } from "@/components/field"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { frontFeedbacks } from "@/routes"

/** Navbar link that opens a dialog to send feedback about the current page. */
export function FeedbackDialog({ pageContext }: { pageContext: string }) {
  const [open, setOpen] = useState(false)
  const form = useForm({ body: "" })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.transform((data) => ({ feedback: { ...data, page_context: pageContext } }))
    form.post(frontFeedbacks.create().url, {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        setOpen(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Icon only on phones, where the text would run into the centered logo. */}
      <DialogTrigger
        aria-label="Feedback?"
        className="flex items-center rounded-lg p-2 text-sm font-medium text-inverse-muted-foreground outline-none hover:text-inverse-foreground focus-visible:ring-2 focus-visible:ring-inverse-foreground sm:mr-3 sm:p-0"
      >
        <MessageSquare className="size-5 sm:hidden" aria-hidden="true" />
        <span className="hidden sm:inline">Feedback?</span>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Feedback?</DialogTitle>
            <DialogDescription>Tell us what would make this page better.</DialogDescription>
          </DialogHeader>
          <Field id="feedback-body" label="Feedback" errors={form.errors.body}>
            <Textarea
              id="feedback-body"
              required
              autoFocus
              className="min-h-40"
              value={form.data.body}
              onChange={(event) => form.setData("body", event.target.value)}
              aria-invalid={form.errors.body ? true : undefined}
              aria-describedby={form.errors.body ? fieldErrorId("feedback-body") : undefined}
            />
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? "Sending..." : "Send feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
